import { lazy, Suspense, useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { InputMethodProvider } from "@/contexts/InputMethodContext";
import { FeatureFlagProvider } from "@/contexts/FeatureFlagContext";
import { DesignTokenProvider } from "@/contexts/DesignTokenContext";
import { ElementOverrideProvider } from "@/contexts/ElementOverrideContext";
import { ElementSelectionOverlay } from "@/components/settings/ElementSelectionOverlay";
import { ScanEngineProvider } from "@/contexts/ScanEngineContext";
import { ElementEditorPanel } from "@/components/settings/ElementEditorPanel";
import InputMethodOverlay from "@/components/input-methods/InputMethodOverlay";
import HeadTrackingEngine from "@/components/input-methods/HeadTrackingEngine";
import GazeTrackingEngine from "@/components/input-methods/GazeTrackingEngine";
import { MouthGestureEngine } from "@/components/input-methods/MouthGestureEngine";
import VoiceControlOverlay from "@/components/voice-control/VoiceControlOverlay";
import { NightMonitoringEngine } from "@/components/night-monitoring/NightMonitoringEngine";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { getUserSettings } from "@/lib/userSettings";
import { FeatureGuard } from "@/components/FeatureGuard";
import { Palette } from "lucide-react";
const DesignAdminPanel = lazy(() => import("@/components/settings/DesignAdminPanel"));
import Index from "./pages/Index";
import CommunicationBoard from "./pages/CommunicationBoard";
import BoardBuilder from "./pages/BoardBuilder";
import Games from "./pages/Games";
import Settings from "./pages/Settings";
import Keyboard from "./pages/Keyboard";
import Translator from "./pages/Translator";
import TranslatorLanguagePicker from "./pages/TranslatorLanguagePicker";
import Onboarding from "./pages/Onboarding";
import Contacts from "./pages/Contacts";
import YouTube from "./pages/YouTube";
import Spotify from "./pages/Spotify";
import Control from "./pages/Control";
import EyeControlSettings from "./pages/EyeControlSettings";
import ActionMapping from "./pages/ActionMapping";
import HeadTrackingSetup from "./pages/HeadTrackingSetup";
import GazeTrackingSetup from "./pages/GazeTrackingSetup";
import ExternalSwitchSetup from "./pages/ExternalSwitchSetup";
import VoiceControlSetup from "./pages/VoiceControlSetup";
import MouthGestureSetup from "./pages/MouthGestureSetup";
import Radio from "./pages/Radio";
import TV from "./pages/TV";
import AACKeyboardPage from "./pages/AACKeyboard";
import SendFlow from "./pages/SendFlow";
import AACKeyboardSend from "./pages/AACKeyboardSend";
import AIChat from "./pages/AIChat";
import SmartHome from "./pages/SmartHome";
import Legal from "./pages/Legal";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import RefundPolicy from "./pages/RefundPolicy";
import Guide from "./pages/Guide";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import VerifyEmail from "./pages/VerifyEmail";
import Pricing from "./pages/Pricing";
import NotFound from "./pages/NotFound";
import ApiDocs from "./pages/ApiDocs";
import AACActions from "./pages/AACActions";
import BoardCellEdit from "./pages/BoardCellEdit";
import ContactsCompose from "./pages/ContactsCompose";
import ContactsSettings from "./pages/ContactsSettings";
import Emergency from "./pages/Emergency";
import Rest from "./pages/Rest";
import VoiceCloning from "./pages/VoiceCloning";
import SmartHomeButtonEdit from "./pages/SmartHomeButtonEdit";
import BoardDetails from "./pages/BoardDetails";
import TwoFactorSetupPage from "./pages/TwoFactorSetupPage";
import CaregiverDelete from "./pages/CaregiverDelete";
import UserContacts from "./pages/UserContacts";
import CookieSettings from "./pages/CookieSettings";
import KeyboardActions from "./pages/KeyboardActions";
import TimeInfo from "./pages/TimeInfo";
import VolumeControl from "./pages/VolumeControl";
import Help from "./pages/Help";
import DevRoutes from "./pages/DevRoutes";
import Entertainment from "./pages/Entertainment";
import BoardEditor from "./pages/BoardEditor";
import HomeEditor from "./pages/HomeEditor";
import EntertainmentEditor from "./pages/EntertainmentEditor";
import KeyboardEditor from "./pages/KeyboardEditor";
import KeyboardPhrasesEditor from "./pages/KeyboardPhrasesEditor";
import CommunicationActionBarEditor from "./pages/CommunicationActionBarEditor";
// Lazy load CaregiverConnect to avoid Firebase initialization on app load
const CaregiverConnect = lazy(() => import("./pages/CaregiverConnect"));

// Helper component that combines FeatureGuard + ProtectedRoute
function FeatureProtectedRoute({ feature, children }: { feature: string; children: React.ReactNode }) {
  return (
    <FeatureGuard name={feature} fallback={<NotFound />}>
      <ProtectedRoute>{children}</ProtectedRoute>
    </FeatureGuard>
  );
}

const queryClient = new QueryClient();

// Apply initial settings
const applyInitialSettings = () => {
  const settings = getUserSettings();
  
  // Apply theme
  document.documentElement.classList.toggle("dark", settings.theme === "dark");
  
  // Font size is now applied per-route in MainLayout (user-facing screens only)
};

applyInitialSettings();

function DesignPanelGlobal() {
  const [panelState, setPanelState] = useState<"closed" | "open" | "minimized">("closed");

  useEffect(() => {
    const handler = () => setPanelState("open");
    window.addEventListener("open-design-panel", handler);
    return () => window.removeEventListener("open-design-panel", handler);
  }, []);

  if (panelState === "closed") return null;

  if (panelState === "minimized") {
    return (
      <button
        onClick={() => setPanelState("open")}
        className="fixed z-[9999] bottom-4 right-4 w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
        title="Open Design Panel"
      >
        <Palette className="w-5 h-5" />
      </button>
    );
  }

  return (
    <Suspense fallback={null}>
      <DesignAdminPanel
        onClose={() => setPanelState("closed")}
        onMinimize={() => setPanelState("minimized")}
      />
    </Suspense>
  );
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <FeatureFlagProvider>
      <DesignTokenProvider>
      <ElementOverrideProvider>
      <InputMethodProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScanEngineProvider>
          <InputMethodOverlay />
          <HeadTrackingEngine />
          <GazeTrackingEngine />
          <MouthGestureEngine />
          <VoiceControlOverlay />
          <NightMonitoringEngine />
          <DesignPanelGlobal />
          <ElementSelectionOverlay />
          <ElementEditorPanel />
          <Routes>
            {/* Public routes */}
            <Route path="/dev-routes" element={<DevRoutes />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/pricing" element={<FeatureGuard name="pricing" fallback={<NotFound />}><Pricing /></FeatureGuard>} />
            <Route path="/api-docs" element={<FeatureGuard name="apiDocs" fallback={<NotFound />}><ApiDocs /></FeatureGuard>} />
            <Route path="/guide" element={<FeatureGuard name="guide" fallback={<NotFound />}><Guide /></FeatureGuard>} />
            <Route path="/landing" element={<Landing />} />
            <Route path="/pair" element={
              <FeatureGuard name="caregiverConnect" fallback={<NotFound />}>
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <CaregiverConnect />
                </Suspense>
              </FeatureGuard>
            } />
            <Route path="/c/:code" element={
              <FeatureGuard name="caregiverConnect" fallback={<NotFound />}>
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <CaregiverConnect />
                </Suspense>
              </FeatureGuard>
            } />
            <Route path="/connect-caregiver/:patientId/:name?" element={
              <FeatureGuard name="caregiverConnect" fallback={<NotFound />}>
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <CaregiverConnect />
                </Suspense>
              </FeatureGuard>
            } />
            {/* Legacy query-param route for backward compatibility */}
            <Route path="/connect-caregiver" element={
              <FeatureGuard name="caregiverConnect" fallback={<NotFound />}>
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <CaregiverConnect />
                </Suspense>
              </FeatureGuard>
            } />
            
            {/* Protected routes */}
            <Route path="/onboarding" element={<FeatureProtectedRoute feature="onboarding"><Onboarding /></FeatureProtectedRoute>} />
            <Route path="/legal" element={<FeatureProtectedRoute feature="legal"><Legal /></FeatureProtectedRoute>} />
            <Route path="/legal/terms" element={<TermsAndConditions />} />
            <Route path="/legal/privacy" element={<PrivacyPolicy />} />
            <Route path="/legal/cookies" element={<FeatureProtectedRoute feature="legal"><CookiePolicy /></FeatureProtectedRoute>} />
            <Route path="/legal/refund" element={<RefundPolicy />} />
            <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
            <Route path="/communication" element={<FeatureProtectedRoute feature="communicationBoard"><CommunicationBoard /></FeatureProtectedRoute>} />
            <Route path="/communication/actions" element={<FeatureProtectedRoute feature="communicationBoard"><AACActions /></FeatureProtectedRoute>} />
            <Route path="/communication/builder" element={<FeatureProtectedRoute feature="boardBuilder"><BoardBuilder /></FeatureProtectedRoute>} />
            <Route path="/games" element={<FeatureProtectedRoute feature="games"><Games /></FeatureProtectedRoute>} />
            <Route path="/settings" element={<FeatureProtectedRoute feature="settings"><Settings /></FeatureProtectedRoute>} />
            <Route path="/keyboard" element={<FeatureProtectedRoute feature="keyboard"><Keyboard /></FeatureProtectedRoute>} />
            <Route path="/aac-keyboard" element={<FeatureProtectedRoute feature="aacKeyboard"><AACKeyboardPage /></FeatureProtectedRoute>} />
            <Route path="/aac-keyboard/send" element={<FeatureProtectedRoute feature="aacKeyboard"><AACKeyboardSend /></FeatureProtectedRoute>} />
            <Route path="/aac-keyboard/send-flow" element={<FeatureProtectedRoute feature="sendFlow"><SendFlow /></FeatureProtectedRoute>} />
            <Route path="/ai-chat" element={<FeatureProtectedRoute feature="aiChat"><AIChat /></FeatureProtectedRoute>} />
            <Route path="/translator" element={<FeatureProtectedRoute feature="translator"><Translator /></FeatureProtectedRoute>} />
            <Route path="/translator/language" element={<FeatureProtectedRoute feature="translator"><TranslatorLanguagePicker /></FeatureProtectedRoute>} />
            <Route path="/contacts" element={<FeatureProtectedRoute feature="contacts"><Contacts /></FeatureProtectedRoute>} />
            <Route path="/contacts/settings" element={<FeatureProtectedRoute feature="contacts"><ContactsSettings /></FeatureProtectedRoute>} />
            <Route path="/contacts/compose" element={<FeatureProtectedRoute feature="contacts"><ContactsCompose /></FeatureProtectedRoute>} />
            <Route path="/history" element={<ProtectedRoute><ComingSoon title="היסטוריה" /></ProtectedRoute>} />
            <Route path="/youtube" element={<FeatureProtectedRoute feature="youtube"><YouTube /></FeatureProtectedRoute>} />
            <Route path="/music" element={<FeatureProtectedRoute feature="music"><Spotify /></FeatureProtectedRoute>} />
            <Route path="/tv" element={<FeatureProtectedRoute feature="tv"><TV /></FeatureProtectedRoute>} />
            <Route path="/radio" element={<FeatureProtectedRoute feature="radio"><Radio /></FeatureProtectedRoute>} />
            <Route path="/entertainment" element={<FeatureProtectedRoute feature="entertainment"><Entertainment /></FeatureProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute><ComingSoon title="יומן" /></ProtectedRoute>} />
            <Route path="/news" element={<ProtectedRoute><ComingSoon title="חדשות" /></ProtectedRoute>} />
            
            <Route path="/smarthome" element={<FeatureProtectedRoute feature="smartHome"><SmartHome /></FeatureProtectedRoute>} />
            <Route path="/smarthome/button" element={<FeatureProtectedRoute feature="smartHome"><SmartHomeButtonEdit /></FeatureProtectedRoute>} />
            <Route path="/communication/builder/details" element={<FeatureProtectedRoute feature="boardBuilder"><BoardDetails /></FeatureProtectedRoute>} />
            <Route path="/more" element={<ProtectedRoute><ComingSoon title="עוד" /></ProtectedRoute>} />
            <Route path="/control" element={<ProtectedRoute><Control /></ProtectedRoute>} />
            <Route path="/control/eye-control" element={<FeatureProtectedRoute feature="eyeControl"><EyeControlSettings /></FeatureProtectedRoute>} />
            <Route path="/control/eye-control/mapping" element={<FeatureProtectedRoute feature="eyeControl"><ActionMapping /></FeatureProtectedRoute>} />
            <Route path="/control/head-tracking" element={<FeatureProtectedRoute feature="headTracking"><HeadTrackingSetup /></FeatureProtectedRoute>} />
            <Route path="/control/gaze-tracking" element={<FeatureProtectedRoute feature="gazeTracking"><GazeTrackingSetup /></FeatureProtectedRoute>} />
            <Route path="/control/external-switch" element={<FeatureProtectedRoute feature="externalSwitch"><ExternalSwitchSetup /></FeatureProtectedRoute>} />
            <Route path="/control/voice" element={<FeatureProtectedRoute feature="voiceControl"><VoiceControlSetup /></FeatureProtectedRoute>} />
            <Route path="/control/mouth-gestures" element={<FeatureProtectedRoute feature="mouthGestures"><MouthGestureSetup /></FeatureProtectedRoute>} />
            
            {/* System pages (converted from modals) */}
            <Route path="/rest" element={<FeatureProtectedRoute feature="rest"><Rest /></FeatureProtectedRoute>} />
            <Route path="/time" element={<FeatureProtectedRoute feature="time"><TimeInfo /></FeatureProtectedRoute>} />
            <Route path="/volume" element={<FeatureProtectedRoute feature="volume"><VolumeControl /></FeatureProtectedRoute>} />
            <Route path="/emergency" element={<FeatureProtectedRoute feature="emergency"><Emergency /></FeatureProtectedRoute>} />
            <Route path="/help" element={<FeatureProtectedRoute feature="help"><Help /></FeatureProtectedRoute>} />
            <Route path="/settings/voice/cloning" element={<FeatureProtectedRoute feature="voiceCloning"><VoiceCloning /></FeatureProtectedRoute>} />
            <Route path="/settings/2fa" element={<FeatureProtectedRoute feature="twoFactorAuth"><TwoFactorSetupPage /></FeatureProtectedRoute>} />
            <Route path="/settings/caregiver/delete" element={<FeatureProtectedRoute feature="caregiverConnect"><CaregiverDelete /></FeatureProtectedRoute>} />
            <Route path="/user-contacts" element={<ProtectedRoute><UserContacts /></ProtectedRoute>} />
            <Route path="/cookies" element={<FeatureProtectedRoute feature="cookieSettings"><CookieSettings /></FeatureProtectedRoute>} />
            <Route path="/keyboard/actions" element={<FeatureProtectedRoute feature="keyboard"><KeyboardActions /></FeatureProtectedRoute>} />
            <Route path="/board-editor" element={<FeatureProtectedRoute feature="boardEditor"><BoardEditor /></FeatureProtectedRoute>} />
            <Route path="/home-editor" element={<FeatureProtectedRoute feature="homeEditor"><HomeEditor /></FeatureProtectedRoute>} />
            <Route path="/entertainment-editor" element={<FeatureProtectedRoute feature="entertainmentEditor"><EntertainmentEditor /></FeatureProtectedRoute>} />
            <Route path="/keyboard-editor" element={<FeatureProtectedRoute feature="keyboardEditor"><KeyboardEditor /></FeatureProtectedRoute>} />
            <Route path="/keyboard-phrases-editor" element={<FeatureProtectedRoute feature="phrasesEditor"><KeyboardPhrasesEditor /></FeatureProtectedRoute>} />
            <Route path="/communication-action-bar-editor" element={<FeatureProtectedRoute feature="actionBarEditor"><CommunicationActionBarEditor /></FeatureProtectedRoute>} />
            
            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </ScanEngineProvider>
        </BrowserRouter>
      </TooltipProvider>
      </InputMethodProvider>
      </ElementOverrideProvider>
      </DesignTokenProvider>
      </FeatureFlagProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);
// Coming Soon placeholder component
function ComingSoon({ title }: { title: string }) {
  return (
    <MainLayout>
      <div className="flex-1 flex flex-col items-center justify-center bg-background" dir="rtl">
        <div className="text-center space-y-4">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <span className="text-4xl">🚧</span>
          </div>
          <h1 className="text-3xl font-bold text-foreground">{title}</h1>
          <p className="text-lg text-muted-foreground">הדף יהיה זמין בקרוב</p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity"
          >
            חזרה לדף הבית
          </a>
        </div>
      </div>
    </MainLayout>
  );
}

export default App;
